import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import type { ReceiptPrefill } from '@/lib/prefill-receipt';
import { useActiveContextValue } from '@/lib/active-context-store';
import { draftKey } from '@/lib/local-drafts';
import type { NoteType } from '../reports/types';
import type { ExpenseForm, Proposals } from './form-model';

/** Who and where the expense is for: saved with the fields, checked again on restore. */
export interface RecoveryTarget {
  /** «Per conto di»: the chosen owner's id, `''` for the caller. */
  readonly ownerUserId: string;
  /** «Nota»: the report the new expense is filed in, `''` for none. */
  readonly reportId: string;
  /** The report's type, as last known; the report field re-reads it. */
  readonly reportNoteType: NoteType | null;
}

/**
 * What is kept: the fields, the AI's proposals (so their marks survive, U15), the reading they came
 * from (a save after a reload still records it) and the target.
 */
export interface Kept {
  readonly form: ExpenseForm;
  readonly proposals: Proposals;
  readonly prefill: ReceiptPrefill | null;
  readonly target: RecoveryTarget;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

function read(key: string): Kept | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isObject(parsed) || !isObject(parsed['form'])) return null;
    const { form, proposals, prefill, target } = parsed;
    const kept = isObject(target) ? target : {};
    return {
      form: form as unknown as ExpenseForm,
      proposals: isObject(proposals) ? proposals : {},
      prefill: isObject(prefill) ? (prefill as unknown as ReceiptPrefill) : null,
      target: {
        ownerUserId: typeof kept['ownerUserId'] === 'string' ? kept['ownerUserId'] : '',
        reportId: typeof kept['reportId'] === 'string' ? kept['reportId'] : '',
        reportNoteType:
          typeof kept['reportNoteType'] === 'string' ? (kept['reportNoteType'] as NoteType) : null,
      },
    };
  } catch {
    return null;
  }
}

function write(key: string, kept: Kept | null): void {
  try {
    if (kept === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(kept));
  } catch {
    // Storage full or blocked: the form still works, it is just not kept.
  }
}

/**
 * What a change is, for the comparison: the fields without the tax lines' keys, which only tell
 * the rows apart on screen and are new each time a form is built, and the owner and report.
 */
function comparable(form: ExpenseForm, target: RecoveryTarget): string {
  return JSON.stringify({
    ...form,
    taxLines: form.taxLines.map(({ key: _key, ...line }) => line),
    ownerUserId: target.ownerUserId,
    reportId: target.reportId,
  });
}

/** The storage key of this person's draft in the active organization; null when either is unknown. */
function useDraftKey(draftId: string): string | null {
  const userId = useAuth().user?.uid;
  const context = useActiveContextValue();
  if (userId === undefined || context === null) return null;
  return draftKey('expense-form', { userId, ...context }, draftId);
}

/**
 * A local copy of the form while it differs from `baseline` (decision 182, U10): reloading or
 * coming back restores what was typed. The copy belongs to the signed-in person in the active
 * organization; another organization reads its own copy, and signing out drops them all. It is
 * dropped when the form is back to the baseline and on `clear()` (after a save). The fields, the
 * proposals, the receipt reading and the target are kept, never the document.
 */
export function useFormRecovery({
  draftId,
  form,
  proposals,
  prefill,
  target,
  baseline,
  ready,
  onRestore,
  onReset,
}: {
  /** The expense's id, or `'new'`. */
  readonly draftId: string;
  readonly form: ExpenseForm;
  readonly proposals: Proposals;
  readonly prefill: ReceiptPrefill | null;
  readonly target: RecoveryTarget;
  /** The form and target as loaded: equal to them, the form is clean. */
  readonly baseline: { readonly form: ExpenseForm; readonly target: RecoveryTarget };
  readonly ready: boolean;
  readonly onRestore: (kept: Kept) => void;
  /** Another person or organization: back to the baseline before reading its own copy. */
  readonly onReset: () => void;
}) {
  const storageKey = useDraftKey(draftId);
  /** The key the form was last read from; `undefined` before the first read. */
  const [hydratedFor, setHydratedFor] = useState<string | null | undefined>(undefined);
  const [restored, setRestored] = useState(false);
  const cleared = useRef(false);
  const callbacks = useRef({ onRestore, onReset });
  callbacks.current = { onRestore, onReset };
  const baselineJson = comparable(baseline.form, baseline.target);
  const dirty = comparable(form, target) !== baselineJson;

  useEffect(() => {
    if (!ready || hydratedFor === storageKey) return;
    if (hydratedFor !== undefined) callbacks.current.onReset();
    const stored = storageKey === null ? null : read(storageKey);
    // Over the baseline: a copy from an older form may lack a field.
    const saved = stored && { ...stored, form: { ...baseline.form, ...stored.form } };
    const differs = saved !== null && comparable(saved.form, saved.target) !== baselineJson;
    if (saved !== null && differs) callbacks.current.onRestore(saved);
    setRestored(differs);
    setHydratedFor(storageKey);
  }, [ready, hydratedFor, storageKey, baseline, baselineJson]);

  useEffect(() => {
    if (storageKey === null || hydratedFor !== storageKey || cleared.current) return;
    write(storageKey, dirty ? { form, proposals, prefill, target } : null);
  }, [hydratedFor, storageKey, dirty, form, proposals, prefill, target]);

  const clear = useCallback(() => {
    cleared.current = true;
    if (storageKey !== null) write(storageKey, null);
  }, [storageKey]);

  const dismissNotice = useCallback(() => setRestored(false), []);

  return { dirty, restored, dismissNotice, clear } as const;
}
